import React, { Ref } from "react";
import Resources from "../../../../types/Resources";
import DateTimeInput from "../../../Common/DateTimeInput";
import TextInput from "../../../Common/TextInput";
import ReadOnlyTextInput from "../../../Common/ReadOnlyTextInput";
import { Transformation } from "../../../../types/functions";
import PermitAgreementFormState from "../../../../types/forms/Permits/EnergyHarbourPermit/PermitAgreementFormState";
import SignatureDisplay from "../../../Signatures/SignatureDisplay";
import BaseUserInfo from "../../../../types/BaseUserInfo";
import { SignatureManager } from "../../../../hooks/useSignature";
import SignatureModal from "../../../Signatures/SignatureModal";
import Button from "../../../Common/Button";
import { DateManager } from "../../../../hooks/useDateInput";
import LabelCheckbox from "../../../Common/LabelCheckbox";

type AgreementsProps = {
    resources: Resources;
    participantCount: number;
    baseUserInfo: BaseUserInfo;

    permitAgreement: PermitAgreementFormState;

    safetyAgreementPermitSeekerSignatureManager: SignatureManager;
    permitAgreementPermitSeekerSignatureManager: SignatureManager;
    permitAgreementPermitProviderSignatureManager: SignatureManager;

    permitAgreementStartDateHandler: DateManager;
    permitAgreementEndDateHandler: DateManager;

    canUpdateEndDate: boolean;
    canManageContent: boolean;
    showUpdateEndDateButton: boolean;
    showColdWorkOnly: boolean;

    onUpdateEndDateButtonClick: () => void;
    onPermitAgreementChange: (fn: Transformation<PermitAgreementFormState>) => void;
};

function Agreements({
    resources,
    participantCount,
    permitAgreement,
    safetyAgreementPermitSeekerSignatureManager,
    permitAgreementPermitSeekerSignatureManager,
    permitAgreementPermitProviderSignatureManager,
    canUpdateEndDate,
    canManageContent,
    permitAgreementStartDateHandler,
    permitAgreementEndDateHandler,
    showUpdateEndDateButton,
    showColdWorkOnly,
    onPermitAgreementChange,
    onUpdateEndDateButtonClick
}: AgreementsProps) {
    function permitAgreementChangeHandler(key: keyof PermitAgreementFormState, value: any) {
        onPermitAgreementChange((prevValue) => ({
            ...prevValue,
            [key]: {
                ...prevValue[key],
                value
            }
        }));
    }

    return (
        <>
            <div className="text-bg-light mt-3 rounded p-3">
                <div>
                    <h1 className="h5">{resources.get("SafetyAgreementHeader")}</h1>
                    <h2 className="h6">{resources.get("SafetyAgreementSubHeader")}</h2>
                    <div className="row">
                        <SignatureDisplay
                            resources={resources}
                            signatureManager={safetyAgreementPermitSeekerSignatureManager}
                            signatureLabel={resources.get("PermitSeekerSignatureLabel")}
                            containerClassName="ms-3"
                            showDate
                        />
                    </div>
                </div>

                <div className="mt-5">
                    <h1 className="h5">{resources.get("PermitAgreementHeader")}</h1>
                    <h2 className="h6">{resources.get("PermitAgreementSubHeader")}</h2>
                    <div>
                        {showUpdateEndDateButton ? (
                            <div className="d-flex justify-content-end">
                                <Button onClick={onUpdateEndDateButtonClick}>{resources.get("UpdateEndDate")}</Button>
                            </div>
                        ) : null}

                        <div className="row">
                            <DateTimeInput
                                defaultValue={permitAgreement.startDate.value ?? new Date()}
                                id="permit-agreement-start-date"
                                label={resources.get("WorkStartAt")}
                                onBlur={permitAgreementStartDateHandler.blurHandler}
                                className="col-md-6"
                                ref={permitAgreementStartDateHandler.ref}
                                errorMessage={permitAgreement.startDate.errorMessage}
                                readOnly={!canManageContent}
                            />
                            <DateTimeInput
                                defaultValue={permitAgreement.endDate.value ?? new Date()}
                                id="permit-agreement-end-date"
                                label={resources.get("WorkEndAt")}
                                onBlur={permitAgreementEndDateHandler.blurHandler}
                                className="col-md-6"
                                ref={permitAgreementEndDateHandler.ref}
                                errorMessage={permitAgreement.endDate.errorMessage}
                                readOnly={!canUpdateEndDate}
                            />
                        </div>
                        {showColdWorkOnly ? (
                            <LabelCheckbox
                                checked={permitAgreement.isColdWorkOnly.value}
                                label={resources.get("OnlyColdWork")}
                                onChange={(updatedValue) =>
                                    permitAgreementChangeHandler("isColdWorkOnly", updatedValue)
                                }
                                id="permit-agreement__is-cold-work-only"
                                containerClassName="mt-3"
                                labelClassName="fw-bold"
                                readOnly={!canManageContent}
                            />
                        ) : null}

                        <div className="row mt-4">
                            <SignatureDisplay
                                signatureManager={permitAgreementPermitSeekerSignatureManager}
                                resources={resources}
                                signatureLabel={resources.get("PermitSeekerSignatureLabel")}
                                containerClassName="ms-3 me-5"
                            />
                            <SignatureDisplay
                                resources={resources}
                                signatureLabel={resources.get("PermitAgreementPermitProvider")}
                                signatureManager={permitAgreementPermitProviderSignatureManager}
                            />
                        </div>
                        <div className="row mt-4">
                            <ReadOnlyTextInput
                                containerClassName="grayed-out col-md-4"
                                inputId="permit-agreement__participant-count"
                                labelText={resources.get("WorkerCount")}
                                value={participantCount.toString()}
                            />

                            <TextInput
                                containerClassName="col-md-4"
                                errorMessage={permitAgreement.fireGuard.errorMessage}
                                inputId="permit-agreement__fire-guard"
                                labelText={resources.get("FireGuard")}
                                onChange={(updatedValue) => permitAgreementChangeHandler("fireGuard", updatedValue)}
                                value={permitAgreement.fireGuard.value}
                                readOnly={!canManageContent}
                            />

                            <TextInput
                                containerClassName="col-md-4"
                                errorMessage={permitAgreement.wayOfCommunication.errorMessage}
                                inputId="permit-agreement__way-of-communication"
                                labelText={resources.get("WayOfCommunication")}
                                onChange={(updatedValue) =>
                                    permitAgreementChangeHandler("wayOfCommunication", updatedValue)
                                }
                                value={permitAgreement.wayOfCommunication.value}
                                readOnly={!canManageContent}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <SignatureModal
                resources={resources}
                SignatureManager={safetyAgreementPermitSeekerSignatureManager}
                ref={safetyAgreementPermitSeekerSignatureManager.signaturePadRef}
            />
            <SignatureModal
                resources={resources}
                SignatureManager={permitAgreementPermitSeekerSignatureManager}
                ref={permitAgreementPermitSeekerSignatureManager.signaturePadRef}
            />
            <SignatureModal
                resources={resources}
                SignatureManager={permitAgreementPermitProviderSignatureManager}
                ref={permitAgreementPermitProviderSignatureManager.signaturePadRef}
            />
        </>
    );
}

export default Agreements;
